import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import VideoUploadModal from '../../../components/ui/VideoUploadModal';
import VideoPlaybackModal from '../../../components/ui/VideoPlaybackModal';
import { supabase } from '../../../lib/supabase';

const LevelThreeSectionEdit = ({ data, onChange, errors, songId, roleId, videoUrl = '', onVideoUrlChange, handleSaveLevel, handleCancelLevel, levelSaving, levelSaved, hasUnsavedChanges }) => {
  const [activeVideoModal, setActiveVideoModal] = useState(null);
  const [playbackVideo, setPlaybackVideo] = useState(null);

  // Initialize default structure if data is empty or not an array
  const initializeDefaultSections = () => {
    return [
      {
        section: 'Opening Section',
        header: '',
        content: '',
        videoId: null,
        videoUrl: null,
        videoTitle: null
      },
      {
        section: 'Middle Section', 
        header: '',
        content: '',
        videoId: null,
        videoUrl: null,
        videoTitle: null
      },
      {
        section: 'Final Section',
        header: '',
        content: '',
        videoId: null,
        videoUrl: null,
        videoTitle: null
      }
    ];
  };

  // Get current sections or initialize default
  const getCurrentSections = () => {
    if (!data || !Array.isArray(data) || data?.length === 0) {
      return initializeDefaultSections();
    }
    
    // Ensure we have exactly 3 sections with the correct structure
    const sections = [...data];
    const requiredSections = ['Opening Section', 'Middle Section', 'Final Section'];
    
    // Add missing sections and ensure header field exists
    requiredSections?.forEach((sectionName, index) => {
      if (!sections?.[index] || sections?.[index]?.section !== sectionName) {
        sections[index] = {
          section: sectionName,
          header: sections?.[index]?.header || '',
          content: sections?.[index]?.content || '',
          videoId: sections?.[index]?.videoId || null,
          videoUrl: sections?.[index]?.videoUrl || null,
          videoTitle: sections?.[index]?.videoTitle || null
        };
      } else {
        // Ensure existing sections have header field
        sections[index] = {
          ...sections?.[index],
          header: sections?.[index]?.header || ''
        };
      }
    });

    return sections?.slice(0, 3); // Only keep first 3 sections
  };

  const sections = getCurrentSections();

  const handleSectionChange = (sectionIndex, field, value) => {
    const updatedSections = [...sections];
    updatedSections[sectionIndex] = {
      ...updatedSections?.[sectionIndex],
      [field]: value
    };
    onChange(updatedSections);
  };

  const handleVideoUpload = async (sectionIndex, videoData) => {
    try {
      // Update the section with video information
      const updatedSections = [...sections];
      updatedSections[sectionIndex] = {
        ...updatedSections?.[sectionIndex],
        videoId: videoData?.id,
        videoUrl: videoData?.video_url,
        videoTitle: videoData?.title || `${sections?.[sectionIndex]?.section} Video`
      };
      
      onChange(updatedSections);
      setActiveVideoModal(null);
    } catch (error) {
      console.error('Error handling video upload:', error);
    }
  };

  const handleVideoRemove = async (sectionIndex) => {
    const section = sections?.[sectionIndex];
    
    if (section?.videoId) {
      try {
        // Remove video from storage if it exists
        if (section?.videoUrl) {
          const videoPath = section?.videoUrl?.split('/')?.pop();
          await supabase?.storage?.from('song-videos')?.remove([videoPath]);
        }
      } catch (error) {
        console.warn('Error removing video file:', error);
      }
    }

    // Update section to remove video references
    const updatedSections = [...sections];
    updatedSections[sectionIndex] = {
      ...updatedSections?.[sectionIndex],
      videoId: null,
      videoUrl: null,
      videoTitle: null
    };
    
    onChange(updatedSections);
  };

  const handleVideoPlay = (section) => {
    if (section?.videoUrl) {
      setPlaybackVideo({
        url: section?.videoUrl,
        title: section?.videoTitle || `${section?.section} Video`
      });
    }
  };

  return (
    <div className="space-y-8">
      {/* Mux Video URL */}
      <div className="bg-white bg-opacity-10 backdrop-blur-sm border border-white border-opacity-20 rounded-xl p-6">
        <h3 className="text-xl font-heading font-semibold text-white mb-4 flex items-center space-x-2">
          <Icon name="Film" size={24} className="text-blue-200" />
          <span>Level 3 Video</span>
          {hasUnsavedChanges && (
            <span className="inline-flex items-center space-x-1 bg-amber-500 bg-opacity-20 border border-amber-400 border-opacity-50 text-amber-300 text-xs px-2 py-0.5 rounded-full">
              <span className="w-1.5 h-1.5 rounded-full bg-amber-400 inline-block" />
              <span>Unsaved</span>
            </span>
          )}
        </h3>
        <div>
          <label className="block text-sm font-medium text-blue-100 mb-2">
            Mux Playback ID
          </label>
          <input
            type="text"
            value={videoUrl}
            onChange={(e) => onVideoUrlChange?.(e?.target?.value)}
            className="w-full px-4 py-2 bg-white bg-opacity-20 border border-white border-opacity-30 rounded-xl text-white placeholder-blue-200 focus:outline-none focus:ring-2 focus:ring-blue-400"
            placeholder="e.g. DS00Spx1CV902MCtPj5WknGlR017IEmVd"
          />
          <p className="text-blue-300 text-xs mt-1">
            Enter the Mux playback_id from your Mux dashboard or the videos table. Leave blank to hide the video for this level.
          </p>
        </div>
      </div>

      {sections?.map((section, index) => (
        <div key={index} className="border border-white border-opacity-20 rounded-xl p-6 bg-white bg-opacity-5">
          {/* Section Header */}
          <div className="flex items-center justify-between mb-6">
            <h4 className="text-lg font-semibold text-white flex items-center space-x-2">
              <Icon 
                name={index === 0 ? "Play" : index === 1 ? "RotateCcw" : "Square"} 
                size={20} 
                className="text-blue-300" 
              />
              <span>{section?.section}</span>
            </h4>
            
            {/* Video Controls */}
            <div className="flex items-center space-x-2">
              {section?.videoUrl ? (
                <>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleVideoPlay(section)}
                    className="text-green-300 hover:bg-green-500 hover:bg-opacity-20 rounded-lg"
                    iconName="Play"
                    iconPosition="left"
                  >
                    Play Video
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleVideoRemove(index)}
                    className="text-red-300 hover:bg-red-500 hover:bg-opacity-20 rounded-lg"
                    iconName="Trash2"
                    iconPosition="left"
                  >
                    Remove
                  </Button>
                </>
              ) : (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setActiveVideoModal(`section-${index}`)}
                  className="border-blue-400 text-blue-200 hover:bg-blue-500 hover:bg-opacity-20 rounded-lg"
                  iconName="Video"
                  iconPosition="left"
                >
                  Add Video
                </Button>
              )}
            </div>
          </div>

          {/* Video Preview */}
          {section?.videoUrl && (
            <div className="mb-4 p-3 bg-blue-500 bg-opacity-20 border border-blue-400 border-opacity-30 rounded-lg">
              <div className="flex items-center space-x-3">
                <Icon name="Video" size={16} className="text-blue-300" />
                <span className="text-sm text-blue-100">
                  {section?.videoTitle || `${section?.section} Video`}
                </span>
              </div>
            </div>
          )}

          {/* NEW: Header Text Box for each subsection */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-blue-100 mb-2">
              {section?.section} Header
            </label>
            <input
              type="text"
              value={section?.header || ''}
              onChange={(e) => handleSectionChange(index, 'header', e?.target?.value)}
              className="w-full px-4 py-3 bg-white bg-opacity-20 border border-white border-opacity-30 rounded-xl text-white placeholder-blue-200 focus:outline-none focus:ring-2 focus:ring-blue-400"
              placeholder={`Enter ${section?.section?.toLowerCase()} header...`}
            />
          </div>

          {/* Content Text Area */}
          <div>
            <label className="block text-sm font-medium text-blue-100 mb-2">
              {section?.section} Content
            </label>
            <textarea
              value={section?.content || ''}
              onChange={(e) => handleSectionChange(index, 'content', e?.target?.value)}
              className="w-full px-4 py-3 bg-white bg-opacity-20 border border-white border-opacity-30 rounded-xl text-white placeholder-blue-200 focus:outline-none focus:ring-2 focus:ring-blue-400"
              placeholder={`Enter ${section?.section?.toLowerCase()} analysis content...`}
              rows={6}
              style={{ whiteSpace: 'pre-wrap' }}
            />
          </div>
        </div>
      ))}
      
      {errors?.level3 && (
        <p className="text-red-300 text-sm mt-1">{errors?.level3}</p>
      )}

      {/* Video Upload Modal */}
      {activeVideoModal && (
        <VideoUploadModal
          isOpen={true}
          onClose={() => setActiveVideoModal(null)}
          onUpload={(videoData) => {
            const sectionIndex = parseInt(activeVideoModal?.split('-')?.[1]);
            handleVideoUpload(sectionIndex, videoData);
          }}
          songId={songId}
          characterAnalysisId={roleId}
          title={`Add Video for ${sections?.[parseInt(activeVideoModal?.split('-')?.[1] || 0)]?.section}`}
        />
      )}

      {/* Video Playback Modal */}
      {playbackVideo && (
        <VideoPlaybackModal
          isOpen={true}
          onClose={() => setPlaybackVideo(null)}
          videoUrl={playbackVideo?.url}
          videoData={{ url: playbackVideo?.url, title: playbackVideo?.title }}
          onReplaceVideo={() => {
            const sectionIndex = activeVideoModal ? parseInt(activeVideoModal?.split('-')?.[1]) : 0;
            setActiveVideoModal(`section-${sectionIndex}`);
          }}
          level="3"
          subsection="video"
        />
      )}

      {/* Per-Level Save / Cancel */}
      <div className="flex justify-end items-center space-x-3 pt-2">
        <Button
          type="button"
          variant="outline"
          onClick={() => handleCancelLevel?.('level3')}
          className="border-white border-opacity-40 text-white hover:bg-white hover:bg-opacity-10"
        >
          Cancel
        </Button>
        <Button
          type="button"
          disabled={levelSaving?.level3}
          onClick={() => handleSaveLevel?.('level3')}
          iconName={levelSaved?.level3 ? 'Check' : 'Save'}
          iconPosition="left"
          className={`bg-white text-indigo-900 hover:bg-gray-100 ${levelSaving?.level3 ? 'opacity-50 cursor-not-allowed' : ''}`}
        >
          {levelSaving?.level3 ? 'Saving...' : levelSaved?.level3 ? 'Saved!' : 'Save Level 3'}
        </Button>
      </div>
    </div>
  );
};

export default LevelThreeSectionEdit;